import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Container, Stack, Text, Title, PasswordInput, Button, Paper, Anchor } from '@mantine/core'
import { IconLock, IconAlertTriangle } from '@tabler/icons-react'
import { useAuth } from '../hooks/useAuth'

const inputStyles = {
  input: {
    background: 'rgba(10, 22, 40, 0.8)',
    border: '1px solid rgba(0, 255, 255, 0.15)',
    color: '#e0e8f0',
  },
  label: { color: 'var(--mantine-color-gray-4)' },
}

export default function ResetPassword() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('reset_password_token')
  const { resetPassword } = useAuth()
  const [password, setPassword] = useState('')
  const [passwordConfirmation, setPasswordConfirmation] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  if (!token) {
    return (
      <Container size="xs" py="xl">
        <Stack align="center" gap="lg" style={{ minHeight: '50vh', justifyContent: 'center' }}>
          <IconAlertTriangle size={48} color="var(--mantine-color-red-5)" />
          <Title order={2} ff="Orbitron, sans-serif" c="cyan.4">
            Invalid Reset Link
          </Title>
          <Text c="dimmed" ta="center">
            This password reset link is invalid or has expired.
          </Text>
          <Anchor c="cyan.4" onClick={() => navigate('/forgot-password')}>
            Request a new reset link
          </Anchor>
        </Stack>
      </Container>
    )
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (password !== passwordConfirmation) {
      setError('Passwords do not match')
      return
    }

    setLoading(true)
    setError('')

    const result = await resetPassword(token, password, passwordConfirmation)
    setLoading(false)

    if (!result.success) {
      setError(result.error)
    }
  }

  return (
    <Container size="xs" py="xl">
      <Stack align="center" gap="lg" style={{ minHeight: '50vh', justifyContent: 'center' }}>
        <Title order={2} ff="Orbitron, sans-serif" c="cyan.4">
          Set New Password
        </Title>
        <Text c="dimmed" ta="center" maw={360}>
          Choose a new password for your account.
        </Text>

        <Paper p="xl" className="glass" w="100%">
          <form onSubmit={handleSubmit}>
            <Stack gap="md">
              <PasswordInput
                label="New Password"
                placeholder="Enter new password"
                leftSection={<IconLock size={16} />}
                value={password}
                onChange={(e) => setPassword(e.currentTarget.value)}
                required
                minLength={6}
                styles={inputStyles}
              />

              <PasswordInput
                label="Confirm Password"
                placeholder="Confirm new password"
                leftSection={<IconLock size={16} />}
                value={passwordConfirmation}
                onChange={(e) => setPasswordConfirmation(e.currentTarget.value)}
                required
                minLength={6}
                error={error}
                styles={inputStyles}
              />

              <Button type="submit" loading={loading} fullWidth size="md">
                Reset Password
              </Button>
            </Stack>
          </form>
        </Paper>

        <Text size="sm" c="dimmed">
          Remember your password?{' '}
          <Anchor c="cyan.4" onClick={() => navigate('/login')}>
            Log in
          </Anchor>
        </Text>
      </Stack>
    </Container>
  )
}
